import React, { useCallback, useEffect } from 'react';
import * as Styles from './GroupedCards.styles';
import { IconLoading, Tag } from 'gatorcomponents';
import { CardGroup } from './components/CardGroup';
import { STATUS_TYPE } from '@/enums/sites/status.enum';
import withErrorBoundary from '@/utils/errorBoundary';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';
import { cloudflareAnalytics } from '@/analytics/cloudflare';
import { useDispatch, useSelector } from 'react-redux';
import { sitesActions } from '@/redux/modules';
import useLocale from '@/hooks/useLocale';
import { useHistory } from 'react-router';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ATIVACAO_CHANGE_DOMAIN_BLOCK_MODAL } from '@/config/GrowthBook/constants';

const GroupedCards = ({
  groupedCards,
  propagationStatus,
  propagationAlertLabels,
  loadingCpanelSSOProductId,
  handleClickCpanel,
  cloudflareLabel,
  tagNewLabel,
  handleGoToCloudFlare,
  manageLabel,
  handleManage,
  getDropdownActions,
  getMainActions,
  loadMoreLabel,
  handlePayInvoice,
  closerPayment,
  loading,
  siteTagLabels,
  hostCount,
  emptyMessage,
  suspendedCpanelTooltip,
  handleEditSite,
  loadingRedirect,
  editSiteCta,
  changeDomainLabel,
  changeDomainFlow,
  temporaryDomains,
  handleShowTempDomainModal,
  setChangeDomainBlockModal,
}) => {
  const { routes } = useLocale();
  const history = useHistory();
  const { domainData, siteBuilderToolsLoading } = useSelector(state => state.sites);

  const dispatch = useDispatch();

  const enabledChangeDomainBlockModal = useFeatureIsOn(ATIVACAO_CHANGE_DOMAIN_BLOCK_MODAL);

  const redirectToChangeDomain = useCallback(({ domain, hostingId }) => {
    history.push(`${routes.sitesPage}${routes.changeDomain}/${hostingId}/${domain}`);
  }, [history, routes]);

  const handleGoToChangeDomain = useCallback(({ domain, hostingId }) => {
    if (!enabledChangeDomainBlockModal) {
      return redirectToChangeDomain({ domain, hostingId });
    }
    dispatch(sitesActions.domainBuilderTools.request({
      domain,
      hostingId,
      redirect: () => redirectToChangeDomain({ domain, hostingId }),
    }));
  }, [dispatch, redirectToChangeDomain, enabledChangeDomainBlockModal]);

  const handleChangeDomainBlockModal = useCallback(() => {
    const blockedTools = ['site_builder', 'site_builder_unpublished'];
    const isBlocked = blockedTools.includes(domainData.tool);

    if (isBlocked && !siteBuilderToolsLoading) {
      return setChangeDomainBlockModal(true);
    }
  }, [domainData, setChangeDomainBlockModal, siteBuilderToolsLoading]);

  useEffect(() => {
    handleChangeDomainBlockModal();
  }, [handleChangeDomainBlockModal]);

  const getCardGroupProps = useCallback((group) => {
    const defaultProps = {
      group,
      propagationStatus,
      propagationAlertLabels,
      getDropdownActions,
      getMainActions,
      loadMoreLabel,
      handlePayInvoice,
      closerPayment,
      loading,
      siteTagLabels,
      hostCount,
      handleEditSite,
      loadingRedirect,
      editSiteCta,
      changeDomainLabel,
      changeDomainFlow,
      temporaryDomains,
    };

    const isSharedHost = HgProducts.SHARED_ALL.includes(group[0].package_id);
    const isRessellerHost = HgProducts.RESELLER_ALL.includes(group[0].package_id);
    const isPlusHost = HgProducts.PLUS_ALL.includes(group[0].package_id);

    const displayChangeDomain = changeDomainFlow && (isSharedHost || isRessellerHost || isPlusHost);

    const handleAccessCpanel = (group) => {
      handleClickCpanel && handleClickCpanel(group);
    };

    const handleClickGoToCloudflare = (group) => {
      cloudflareAnalytics.sitesButton();

      const findTempDomain = temporaryDomains && temporaryDomains.filter(item => item.hosting_id === group[0].hosting_id);
      if (findTempDomain && findTempDomain.length) {
        handleShowTempDomainModal({ temporaryDomains: findTempDomain, label: cloudflareLabel });
      }

      if (!findTempDomain || (findTempDomain && !findTempDomain.length)) {
        handleGoToCloudFlare && handleGoToCloudFlare(group);
      }
    };

    const actions = (group, planStatus) => {
      const actions = [];
      const canDisplayCloudflareButton = group[0].cloudflare_available
        && group[0].status.toLowerCase() === STATUS_TYPE.ACTIVE;

      const currentCpanelIsLoading = loadingCpanelSSOProductId === group[0].hosting_id;

      if (canDisplayCloudflareButton) {
        actions.push({
          label: (
            <Styles.NewActionButtonWrapper>
              {cloudflareLabel}
              <Tag label={tagNewLabel} size="sm" />
            </Styles.NewActionButtonWrapper>
          ),
          onClick: () => handleClickGoToCloudflare(group),
          testId: `cloudflare-${group[0].external_id}-cta`,
        });
      }

      if (isSharedHost) {
        actions.push({
          label: currentCpanelIsLoading
            ? (
              <>
                <IconLoading />
                {' cPanel'}
              </>
            )
            : 'cPanel',
          onClick: () => handleAccessCpanel(group),
          testId: `cPanel-${group[0].external_id}-cta`,
          disabled: [STATUS_TYPE.ANALYSIS, STATUS_TYPE.PENDING, STATUS_TYPE.SUSPENDED].includes(planStatus),
          tooltip: [STATUS_TYPE.SUSPENDED].includes(planStatus) && suspendedCpanelTooltip,
        });
      }

      actions.push({
        label: manageLabel,
        onClick: () => handleManage(group),
        testId: `manage-${group[0].external_id}-cta`,
      });

      return actions;
    };

    return {
      ...defaultProps,
      displayChangeDomain,
      actions,
      handleGoToChangeDomain,
    };
  }, [
    suspendedCpanelTooltip,
    loadingCpanelSSOProductId,
    manageLabel,
    handleManage,
    handleClickCpanel,
    getDropdownActions,
    getMainActions,
    loadMoreLabel,
    loading,
    hostCount,
    closerPayment,
    handlePayInvoice,
    propagationAlertLabels,
    propagationStatus,
    siteTagLabels,
    handleEditSite,
    loadingRedirect,
    editSiteCta,
    cloudflareLabel,
    handleGoToCloudFlare,
    tagNewLabel,
    changeDomainFlow,
    changeDomainLabel,
    handleGoToChangeDomain,
    handleShowTempDomainModal,
    temporaryDomains,
  ]);

  return (
    <Styles.GroupedCardsWrapper data-testid="groupedCardsWrapper">
      {(groupedCards && groupedCards.length > 0) && groupedCards.map(group => (
        group && group.length && (
          <CardGroup
            {...getCardGroupProps(group)}
            key={group[0].external_id
              ? `${group[0].external_id}-external`
              : `${group[0].hosting_id}-hosting`
            }
          />
        )
      ))}

      {(groupedCards && groupedCards.length === 0) && (
        <Styles.EmptyState>
          {emptyMessage}
        </Styles.EmptyState>
      )}
    </Styles.GroupedCardsWrapper>
  );
};

export default withErrorBoundary(GroupedCards, 'GroupedCards');
